import type { SerializedStyles } from '@emotion/core';
import { css } from '@emotion/core';
import styled from '@emotion/styled';

type TypographyProps = {
  fontWeight?: number;
  isTermsContent?: boolean;
  margin?: string;
  padding?: string;
  textAlign?: string;
  textColor?: string;
  fontSize?: string;
  width?: string;
  lineHeight?: string;
};

const isTermsContentStyles = ({
  theme,
}: {
  theme: AppTheme.WeasleyTheme;
}): SerializedStyles => css`
  font-size: ${theme.checkbox.label.terms.fontSize};
  font-weight: ${theme.checkbox.label.terms.fontWeight};
  line-height: ${theme.checkbox.label.terms.lineHeight};
  color: ${theme.checkbox.label.terms.color};
`;

const commonStyles = ({
  fontWeight,
  margin,
  textAlign,
  theme,
  width,
}: {
  fontWeight?: number;
  margin?: string;
  textAlign?: string;
  theme: AppTheme.WeasleyTheme;
  width?: string;
}): SerializedStyles => css`
  color: ${theme.typography.headingH1.color};
  font-family: ${theme.typography.font.family};
  line-height: ${theme.typography.lineHeight.xLarge};
  font-weight: ${fontWeight ? fontWeight : theme.typography.weight.regular};
  margin: ${margin ? margin : 'initial'};
  text-align: ${textAlign ? textAlign : 'initial'};
  width: ${width ? width : 'initial'};
`;

export const StyledHeading = styled.h1<TypographyProps>`
  ${({ theme, fontWeight, margin, textAlign, width }) =>
    commonStyles({ fontWeight, margin, textAlign, theme, width })};
  font-size: ${({ theme, fontSize }) =>
    fontSize ? fontSize : theme.typography.fontSize.xLarge};
`;

export const SecondaryStyledHeading = styled.h2<TypographyProps>`
  ${({ theme, fontWeight, margin, textAlign, width }) =>
    commonStyles({ fontWeight, margin, textAlign, theme, width })};
  font-size: ${({ theme, fontSize }) =>
    fontSize ? fontSize : theme.typography.fontSize.large};
`;
export const StyledHeadingCentered = styled(StyledHeading)<TypographyProps>`
  margin: 1.5rem 0;
  @media screen and (max-width: 420px) {
    text-align: center;
  }
`;
export const StyledSubHeading = styled(SecondaryStyledHeading)<TypographyProps>`
  font-size: ${({ theme, fontSize }) =>
    fontSize ? fontSize : theme.typography.fontSize.regular};
  font-weight: ${({ theme, fontWeight }) =>
    fontWeight ? fontWeight : theme.typography.weight.xBold};
  line-height: ${({ theme, lineHeight }) =>
    lineHeight ? lineHeight : theme.typography.lineHeight.large};
  margin-bottom: 8px;
`;

const commonInlineElementsStyles = ({
  fontSize,
  fontWeight,
  isTermsContent,
  margin,
  padding,
  textAlign,
  textColor,
  theme,
  width,
}: TypographyProps & { theme: AppTheme.WeasleyTheme }): SerializedStyles => css`
  color: ${textColor ? textColor : theme.typography.body.color};
  font-family: ${theme.typography.font.family};
  font-size: ${fontSize ? fontSize : theme.typography.fontSize.regular};
  line-height: ${theme.typography.lineHeight.large};
  font-weight: ${fontWeight ? fontWeight : theme.typography.weight.regular};
  margin: ${margin ? margin : 'initial'};
  padding: ${padding && padding};
  text-align: ${textAlign ? textAlign : 'initial'};
  width: ${width && width};
  ${isTermsContent && isTermsContentStyles({ theme })};
`;

export const StyledParagraph = styled.p<TypographyProps>`
  ${props => commonInlineElementsStyles({ ...props })};
`;

export const StyledSpan = styled.span<TypographyProps>`
  ${props => commonInlineElementsStyles({ ...props })};
  display: inline-block;
`;

export const StyledHelperText = styled(StyledParagraph)<TypographyProps>`
  color: ${({ theme }) => theme.typography.textHelper.color};
  font-size: ${({ theme, fontSize }) =>
    fontSize ? fontSize : theme.typography.fontSize.small};
  line-height: ${({ theme }) => theme.typography.lineHeight.medium};
`;
